import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { roleFromAccessToken } from "@/lib/jwt";
import { Turnstile } from "@/components/Turnstile";

function decodeJwtPayload(token: string | undefined | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
  } catch { return null; }
}
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});
type FormData = z.infer<typeof schema>;

export default function AdminLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");

  useEffect(() => {
    const state = loc.state as { notAdmin?: boolean; idleLogout?: boolean } | null;
    if (state?.notAdmin) {
      setServerError(
        "Your session doesn't have admin access. Sign in again with admin credentials.",
      );
    } else if (state?.idleLogout) {
      setServerError("You were signed out for inactivity. Sign back in to continue.");
    }
  }, [loc.state]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormData) {
    setServerError(null);
    setDiagnostics(null);

    await supabase.auth.signOut();

    if (!captchaToken) {
      setServerError("Please complete the verification challenge.");
      return;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      ...values,
      options: { captchaToken },
    });
    if (error) {
      // Log the real error so future debugging is easier; show a generic
      // message to the user unless it's clearly a captcha problem.
      console.error("Admin signin error:", error);
      const msg = error.message ?? "";
      if (msg.toLowerCase().includes("captcha")) {
        setServerError("Verification failed. Refresh the page and try again.");
      } else {
        setServerError("Invalid email or password.");
      }
      return;
    }

    const token = data.session?.access_token;
    const role = roleFromAccessToken(token);
    if (role !== "admin") {
      const payload = decodeJwtPayload(token);
      const snippet = {
        role_field:     payload?.role ?? "(none)",
        app_metadata:   payload?.app_metadata ?? "(none)",
        user_metadata_keys: Object.keys((payload?.user_metadata as object) ?? {}),
        sub:            payload?.sub,
        iat:            payload?.iat,
      };
      setServerError(
        `Sign-in succeeded but the JWT is missing app_metadata.role = "admin".`,
      );
      setDiagnostics(JSON.stringify(snippet, null, 2));
      await supabase.auth.signOut();
      return;
    }

    // Password succeeded (aal1). Check for an enrolled TOTP factor and route
    // through MFA. RequireAdmin would do this for us, but we read it here so
    // the user lands on the right screen immediately instead of bouncing.
    const factors = await supabase.auth.mfa.listFactors();
    const verifiedFactor = factors.data?.totp?.find((f) => f.status === "verified");
    if (verifiedFactor) {
      nav("/admin/mfa/challenge", { replace: true });
    } else {
      nav("/admin/mfa/setup", { replace: true });
    }
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
        <div className="flex justify-center mb-1.5">
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-fh-gray bg-fh-sand rounded-full px-2.5 py-0.5">
            Admin
          </span>
        </div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Admin sign in
        </h1>
        <p className="text-[14px] text-fh-gray text-center mb-6">
          Restricted access. All actions are audited.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
          {serverError && <Alert tone="error">{serverError}</Alert>}
          {diagnostics && (
            <pre className="text-[11px] bg-fh-ink text-fh-cream rounded-[8px] p-3 overflow-auto whitespace-pre-wrap break-all">
{diagnostics}
            </pre>
          )}
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoFocus
            error={errors.email?.message}
            {...register("email")}
          />
          <FormField
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            labelRight={
              <Link to="/admin/forgot-password" className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark">
                Forgot?
              </Link>
            }
            {...register("password")}
          />
          <Turnstile siteKey={env.VITE_TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
          <Button type="submit" loading={isSubmitting} fullWidth className="mt-1">
            Sign in
          </Button>
          <p className="text-center text-[13px] text-fh-gray mt-1">
            <Link to="/admin/forgot-password" className="font-semibold text-fh-green hover:text-fh-green-dark">
              Forgot your password?
            </Link>
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}
